// helpers.js
import React, { useEffect, useRef } from 'react';
import { MenuItem, TextField } from '@mui/material';
import { config } from '../../config';
import { formatInTimeZone } from 'date-fns-tz';

export const generateTextField = (label, value, className, readOnly = true, onChange = () => { }, extraProps = {}) => {
    const inputProps = readOnly ? { readOnly: true } : {};
    return (
        <TextField
            label={label}
            variant="standard"
            color="primary"
            value={value}
            onChange={readOnly ? undefined : onChange}
            className={className}
            {...(!readOnly && { InputProps: inputProps })}
            {...extraProps}
            inputProps={{
                ...extraProps.inputProps,
            }}
        />
    );
};

/**
 * Generates an array of objects with `key` and `value` properties from the input array.
 *
 * @param {Array<Object>} inputArray - The array of objects to process.
 * @param {string} keyField - The field name to use for the `key` property.
 * @param {string} valueField - The field name to use for the `value` property.
 * @returns {Array<Object>} An array of objects with `key` and `value` properties.
 */

export function generateKeyValueArray(inputArray, keyField, valueField) {
    // console.log('generateKeyValueArray:', inputArray, keyField, valueField);
    return inputArray.map(item => ({
        key: item[keyField],
        value: item[keyField],
        label: item[valueField]
    }));
}

/**
 * Generates a select field component with options. 
 * Check function `generateKeyValueArray` to generate the options array.
 *
 * @param {Array} options - The array of options for the select field.
 * @param {Object} props - The additional props for the select field component.
 * @returns {JSX.Element} The select field component.
 */

export const generateSelectField = (options = [], props = {}) => {
    const defaultOption = options.find(option => option.default);
    const defaultValue = defaultOption ? defaultOption.value : '';
    // console.log('options:', options);
    // console.log('defaultOption:', defaultOption);
    // console.log('defaultValue:', defaultValue);
    // console.log('props:', props);

    return (
        <TextField
            variant="standard"
            label={defaultOption ? defaultOption.label : ''}
            select
            value={props.value || defaultValue}
            {...props}
        >
            {options.map((option) => (
                <MenuItem key={option.key ? option.key : option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
};

export const generateClassName = (baseClass, suffix, ...additionalClasses) => {
    if (!Array.isArray(additionalClasses)) {
        additionalClasses = [];
    }

    const baseClassNames = baseClass.split(' ').map(cls => `${cls}${suffix}`).join(' ');

    const additionalClassNames = additionalClasses.filter(Boolean).map(cls => {
        if (typeof cls === 'string') {
            return cls.split(' ').map(subCls => `${subCls}${suffix}`).join(' ');
        }
        return '';
    }).join(' ');

    return `${baseClassNames} ${additionalClassNames}`.trim();
};

export const toLocaleDateTime = (date, showTime = false) => {
    if (!date) return '';
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Get timezone from browser
    const formatString = showTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';

    let isoDate;

    if (typeof date === 'string') {
        // Handle string-based date
        isoDate = showTime && !date.endsWith('Z') ? `${date}Z` : date;
    } else if (typeof date === 'object' && date.date && date.time) {
        // Handle object-based date
        const { year, month, day } = date.date;
        const { hour = 0, minute = 0, second = 0 } = date.time;
        isoDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second)).toISOString();
    } else if (typeof date === 'object' && date.year && date.month && date.day) {
        // Handle object-based date without time
        const { year, month, day } = date;
        isoDate = new Date(Date.UTC(year, month - 1, day)).toISOString();
    } else {
        throw new Error('Invalid date format');
    }

    return formatInTimeZone(new Date(isoDate), timeZone, formatString);
};

export const getIsoDate = (date) => {
    if (!date) return null;

    let parsedDate;

    if (typeof date === 'string') {
        // Handle string-based date
        if (date.endsWith('Z') || date.includes('T')) {
            // Already in ISO 8601 format
            parsedDate = new Date(date);
        } else {
            // Append 'Z' if not in ISO format
            parsedDate = new Date(`${date}Z`);
        }
    } else if (typeof date === 'object' && date instanceof Date) {
        // Handle Date object
        parsedDate = date;
    } else if (typeof date === 'object' && date.date && date.time) {
        // Handle object-based date
        const { year, month, day } = date.date;
        const { hour = 0, minute = 0, second = 0 } = date.time;
        parsedDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    } else if (typeof date === 'object' && date.year && date.month && date.day) {
        // Handle object-based date without time
        const { year, month, day } = date;
        parsedDate = new Date(Date.UTC(year, month - 1, day));
    } else {
        console.log('(!) getIsoDate date type:', typeof date, date);
        throw new Error('Invalid date format (getIsoDate)');
    }

    return parsedDate;
};

export const get_YYMMDDHHMM = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);

    return `${year}_${month}_${day}_${hours}${minutes}`;
}

export const objectAsJson = (obj) => {
    return (<pre>{JSON.stringify(obj, null, 2)}</pre>);
};

export const promptForNumericValue = (promptMessage) => {
    const inputValue = prompt(promptMessage);

    if (inputValue !== null) {
        if (!isNaN(inputValue)) {
            const numericValue = parseFloat(inputValue);
            return numericValue; // Return the numeric value for further use
        } else {
            console.error('Invalid number. Please enter a valid numeric value.');
        }
    } else {
        console.log('User canceled the input.');
    }

    // Return null if there's an issue or if the user canceled
    return null;
};

export const callAPIWhileSettingState = async (fetchMethod, paramsArray, setLoading, setDataCallback) => {
    try {
        if (typeof fetchMethod === 'function') {
            if (setLoading) setLoading(true);
            const data = await fetchMethod(...paramsArray);
            if (setDataCallback) setDataCallback(data);
        } else {
            console.log('fetchMethod is not a function');
        }
    } catch (error) {
        console.log(`Error in call to method ${fetchMethod.name || 'unknown'}: `, error);
        throw new Error(error); // Rethrow the error so it can be handled by the calling component
    } finally {
        if (setLoading) setLoading(false);
    }
};

export const fetchAPIDataWhileSettingState = async (fetchMethod, paramsArray, setLoading) => {
    try {
        if (typeof fetchMethod === 'function') {
            if (setLoading) setLoading(true);
            const data = await fetchMethod(...paramsArray);
            return data;
        } else {
            console.log('fetchMethod is not a function');
        }
    } catch (error) {
        console.log(`Error in call to method ${fetchMethod.name || 'unknown'}: `, error);
        throw new Error(error); // Rethrow the error so it can be handled by the calling component
    } finally {
        if (setLoading) setLoading(false);
    }
};

const setNestedProperty = (obj, path, value) => {
    if (typeof path !== 'string') {
        throw new TypeError('Path must be a string');
    }
    const keys = path.split('.');
    const lastKey = keys.pop();
    const lastObj = keys.reduce((acc, key) => acc[key] = acc[key] || {}, obj);
    lastObj[lastKey] = value;
};

/**
 * Retrieves the value at a given path within an object.
 *
 * @param {Object} obj - The object from which to retrieve the value.
 * @param {string} pathOfField - The path of the field, with each level separated by a dot.
 * @returns {*} - The value at the specified path, or undefined if the path does not exist.
 */
export const getValueAtPath = (obj, pathOfField) => {
    return pathOfField.split('.').reduce((acc, part) => acc && acc[part], obj);
};

/**
 * Handles the change of a nested field within an object and updates the state.
 *
 * @param {string} field - The path to the nested field to be updated.
 * @param {*} value - The new value to be set for the specified field.
 * @param {Function} setObjectFields - The state setter function to update the object fields.
 */
export const handleObjectFieldChange = (field, value, setObjectFields) => {
    setObjectFields((prevObjectInfo) => {
        const updatedObjectInfo = { ...prevObjectInfo };
        setNestedProperty(updatedObjectInfo, field, value);
        return updatedObjectInfo;
    });
};

/**
 * Checks if the target object contains all the fields present in the source object.
 * This function performs a deep comparison for nested objects.
 *
 * @param {Object} targetToCheck - The object to check for the presence of fields.
 * @param {Object} sourceForComparison - The object that provides the fields to check against.
 * @returns {boolean} - Returns true if the target object contains all the fields from the source object, otherwise false.
 */
export function hasAllFields(targetToCheck, sourceForComparison) {
    for (let key in sourceForComparison) {
        if (!(key in targetToCheck)) {
            return false;
        }
        if (typeof sourceForComparison[key] === 'object' && sourceForComparison[key] !== null) {
            if (!hasAllFields(targetToCheck[key], sourceForComparison[key])) {
                return false;
            }
        }
    }
    return true;
}

export const focusOnComponent = (componentRef) => {
    if (componentRef.current) {
        componentRef.current.scrollIntoView({ behavior: 'smooth' });
    }
};

export const withAutoFocus = (Component) => {
    const WithAutoFocus = (props) => {
        const ref = useRef();

        useEffect(() => {
            if (ref.current) {
                ref.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, []);

        return <Component ref={ref} {...props} />;
    };

    WithAutoFocus.displayName = `WithAutoFocus(${Component.displayName || Component.name || 'Component'})`;

    return WithAutoFocus;
};

export const fullImagePath = (imagePath) => {
    return `${config.baseB2B_Images}${imagePath}`;
}

export const roundDecimalNumber = (number, decimals = 2) => {
    return Math.round(number * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

export const returnArrayAsContentField = (array) => {
    return { content: array };
}

/**
 * Determines whether a component should be rendered based on the provided login data, allowed roles, and B2B products.
 * 
 * @param {object} loginData - The login data object.
 * @param {string[]} rolesAllowed - An array of allowed roles.
 * @param {string[]} b2bProducts - An array of B2B products.
 * @returns {boolean} - Returns true if the component should be rendered, otherwise false.
 */
export function shouldRenderComponent(loginData, rolesAllowed, b2bProducts) {
    return (
        (loginData?.roles?.includes('ROLE_SUPER_ADMIN') || loginData?.roles?.includes('SUPER_ADMIN')) ||
        ((!rolesAllowed || !rolesAllowed.length || rolesAllowed.some(role => loginData?.roles?.includes(role) || loginData?.roles?.includes('ROLE_' + role))) &&
            (!b2bProducts || !b2bProducts.length || b2bProducts.some(product => loginData?.productAcronyms?.includes(product))))
    );
}

/**
 * Checks if the user has any of the specified roles.
 *
 * @param {Object} loginData - The login data of the user.
 * @param {Array<string>} rolesAllowed - The list of roles to check against.
 * @returns {boolean} - Returns true if the user has any of the specified roles, otherwise false.
 */
export function isUserHavingAnyOfTheseRoles(loginData, rolesAllowed) {
    if (!Array.isArray(rolesAllowed)) {
        console.error('rolesAllowed is not an array:', rolesAllowed);
        return false;
    }
    return rolesAllowed.some(roleAllow => loginData?.roles?.includes(roleAllow) || loginData?.roles?.includes('ROLE_' + roleAllow));
}

/**
 * Compares two arrays and checks if they are equal.
 * @param {Array} arr1 - The first array to compare.
 * @param {Array} arr2 - The second array to compare.
 * @returns {boolean} - Returns true if the arrays are equal, otherwise returns false.
 */
export function arraysAreEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) {
        return false;
    }
    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
            return false;
        }
    }
    return true;
}

/**
 * Truncates the given text to the specified limit.
 * 
 * @param {string} text - The text to be truncated.
 * @param {number} limit - The maximum number of words to display.
 * @returns {string} - The truncated text.
 */
export const truncateText = (text, limit, CRsDetection = false) => {
    if (CRsDetection) {
        text = text.replace(/\n/g, '<br />');
    }
    if (!limit) return text;
    const words = text.split(' ');
    if (words.length <= limit) return text;
    return words.slice(0, limit).join(' ') + '...';
};

export const truncateTextByCharacters = (text, limit) => {
    if (!limit) return text;
    if (text.length <= limit) return text;
    return text.slice(0, limit) + '...';
};

/**
 * Formats a number as a percentage string with a specified number of decimal places.
 *
 * @param {number|string} value - The value to format as a percentage.
 * @param {number} [decimals=2] - The number of decimal places to include in the formatted percentage.
 * @returns {string} The formatted percentage string.
 */
export function formatPercentage(value, decimals = 2) {
    return parseFloat(value || 0).toFixed(decimals);
}

/**
 * Omits the specified fields from the given object or array of objects, including nested fields.
 *
 * @param {Object|Array<Object>} data - The object or array of objects from which to omit fields.
 * @param {Array<string>} fieldsToOmit - The array of fields to omit from the object, including nested fields.
 * @returns {Object|Array<Object>} - A new object or array of objects without the specified fields.
 */
export const omitFields = (data, fieldsToOmit) => {
    if (!data || (typeof data !== 'object' && !Array.isArray(data))) {
        throw new TypeError('First argument must be an object or an array of objects');
    }
    if (!Array.isArray(fieldsToOmit)) {
        throw new TypeError('Second argument must be an array of strings');
    }

    const omitFieldsFromObject = (obj) => {
        const result = { ...obj };

        fieldsToOmit.forEach(field => {
            const keys = field.split('.');
            let current = result;

            for (let i = 0; i < keys.length - 1; i++) {
                if (current[keys[i]] === undefined) {
                    return;
                }
                current = current[keys[i]];
            }

            delete current[keys[keys.length - 1]];
        });

        return result;
    };

    if (Array.isArray(data)) {
        return data.map(item => omitFieldsFromObject(item));
    } else {
        return omitFieldsFromObject(data);
    }
};

/**
 * Parses an XML string into an XML Document object.
 *
 * @param {string} xmlString - The XML string to be parsed.
 * @returns {Document} The parsed XML Document object.
 */
export const parseXmlToJson = (xmlString) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "application/xml");
    // Process the XML document as needed
    return xmlDoc;
};

/**
 * Produces a hash string from a given object.
 * Uses JSON.stringify and a simple djb2 hash algorithm.
 *
 * @param {Object} obj - The object to hash.
 * @returns {string} - The resulting hash string.
 */
function stableStringify(obj) {
    if (Array.isArray(obj)) {
        return `[${obj.map(stableStringify).join(',')}]`;
    } else if (obj && typeof obj === 'object') {
        return `{${Object.keys(obj).sort().map(
            key => `"${key}":${stableStringify(obj[key])}`
        ).join(',')}}`;
    } else {
        return JSON.stringify(obj);
    }
}

export function objectHash(obj) {
    const str = stableStringify(obj);
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) + str.charCodeAt(i); // hash * 33 + c
    }
    return hash.toString(16);
}
